import Axios, {
  AxiosError,
  AxiosResponse, InternalAxiosRequestConfig,
  Method,
} from 'axios';
import {
  API_ROUTES,
  API_ROUTES_FOR_ACTIVE_MEMBER,
  API_ROUTES_FOR_BINDER,
  API_ROUTES_FOR_BROKER,
  API_ROUTES_FOR_CA,
  API_ROUTES_FOR_CA_GOVT,
  API_ROUTES_FOR_GOVT_ACTIVE,
  API_ROUTES_FOR_GOVT_GUEST,
  EXCLUSIVE_ERRORCODE_ROUTES,
  EXCLUSIVE_LOADER_ROUTES,
  PROCESS_ENV_URLS,
  // useMock,
} from './constants';
import { IGlobalTechnicalDifficulty, ISetApiStatus } from '../store/interface';
import { API_STATUS_TYPES, LOADER_SCREEN_ACTION } from '../store/constants';
import { USER_TYPES } from '../constants/common';
// import { mockAPI } from './__mocks__';

interface IApiHeadersProps {
  'Content-Type': string;
  'Cache-Control'?: string;
  'Accept-API-Version'?: string;
  Authorization?: string;
}

const APIHeaders = (
  contentType?: string,
  cacheControl?: string,
  acceptApiVersion?: string,
): IApiHeadersProps => ({
  'Content-Type': contentType ?? 'application/json',
  'Cache-Control': cacheControl ?? undefined,
  'Accept-API-Version': acceptApiVersion ?? undefined,
});

export interface IAPIHeaders {
  MPP_ID?: number;
  STATE_CODE?: string;
  MEMBER_ID?: string;
  SELECTED_CIM?: string;
  SELECTED_CASE?: string;
  EXCHANGE_INDICATOR?: string;
  FUNDING_ACCOUNT_LAST_FOUR_DIGIT?: string;
  SELECTED_PAYMENT_METHOD?: string;
  AUTHORIZATION?: string;
  MESSAGE_ID?: string;
  REQUEST_TIMESTAMP?: string;
  SESSION_TOKEN?: string;
  USER_TYPE_CODE?: number;
  APPLICATION_ID?: string;
  'Cache-Control'?: string;
  'Accept-API-Version'?: string;
  DATA_SOURCE?: string;
  LOB?: string;
}

export interface IBinderIlbDetailsList {
  applicationId: string;
  mppId: number;
  caseDetailNumber?: string;
  cimNumber?: string;
  subscriberCimNumber?: string;
  subscriberCaseNumber?: string;
}

export interface IGetAutoPayments {
  billDetails: IBinderIlbDetailsList[];
  effectiveDate: string;
}

export interface ICancelSchedulePaymentHeader extends IAPIHeaders {
  REFERENCE_ID?: string;
  REFERENCE_CODE?: string;
}

export interface ICancelAutoPaymentHeader extends IAPIHeaders {
  REFERENCE_ID?: string;
  REFERENCE_CODE?: string;
  FREEZECONVERSION?: boolean;
}

export interface IGetAutoPaymentHeader extends IAPIHeaders {
  MID: number;
}

interface IAPIOptions<dataType> {
  method?: Method;
  data?: dataType; // data type based on API
  baseURL?: string; // override the default baseurl
  headers?: IAPIHeaders;
  credentials?: boolean; // to include cookies in request
}

export interface IActiveMemberAPIHeaders {
  USER_TYPE_CODE?: number;
  AUTHORIZATION?: string;
}

// Module-level flag ensuring Axios interceptors are registered exactly once,
// regardless of how many times initialiseAxios() is invoked (see comment
// inside initialiseAxios for details).
let axiosInterceptorsInitialised = false;

const utils = {
  getHeaders: (
    contentType: string,
    cacheControl?: string,
    acceptApiVersion?: string,
  ): IApiHeadersProps => {
    return { ...APIHeaders(contentType, cacheControl, acceptApiVersion) };
  },
  fetch: <dataType>(
    route: string,
    options?: IAPIOptions<dataType>,
    contentType?: string,
  ): Promise<any> => {
    //needs to be commented out if mock is not needed
    // if (useMock) {
    //   return mockAPI(route, options?.data);
    // }
    return Axios(route, {
      url: route,
      baseURL: options?.baseURL || PROCESS_ENV_URLS.baseURL,
      headers: {
        ...utils.getHeaders(contentType!),
        ...(options?.headers || {}),
      },
      data: options?.data,
      method: options?.method,
      withCredentials: options?.credentials,
    });
  },

  initialiseAxios: (
    setAPIStatus: ISetApiStatus,
    setGlobalTechnicalDifficulty: (
      _globalTechnicalDifficulty: IGlobalTechnicalDifficulty,
    ) => void,
  ): any => {
    // Guard against duplicate interceptor registration. This function is
    // invoked from App's render body so the interceptors are guaranteed to
    // be registered before any descendant's mount effect can dispatch an
    // API call. However, React 18 StrictMode intentionally double-invokes
    // component render bodies in development, which would otherwise cause
    // this to run twice and register two sets of interceptors. Since Axios
    // chains response interceptors sequentially, the second interceptor
    // would then receive the first interceptor's already-unwrapped
    // `response.data` as its `response` argument (which has no `.config`),
    // causing a thrown error on every request. This module-level flag makes
    // registration idempotent regardless of how many times it's called.
    if (axiosInterceptorsInitialised) {
      return;
    }
    axiosInterceptorsInitialised = true;

    Axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      processLoader(setAPIStatus, config, true);
      return config;
    });

    Axios.interceptors.response.use(
      (response: AxiosResponse) => {
        processLoader(setAPIStatus, response?.config, false);
        return Promise.resolve(response.data);
      },
        (err: AxiosError) => {
          if (err?.config) {
            processLoader(setAPIStatus, err.config, false);
          }
          return Promise.reject(
              processError(err, setAPIStatus, setGlobalTechnicalDifficulty),
          );
        },
    );
  },
  getUserSpecificAPIRoute: (
    userTypeCode: number,
    isCABinder?: boolean,
  ): any => {
    // return type is any, since default is guest routes
    // and binder routes don't contain common routes of guest
    switch (userTypeCode) {
      case USER_TYPES.GUEST: {
        return API_ROUTES;
      }
      case USER_TYPES.NMA_IFM_USER:
      case USER_TYPES.ONE_TIME_PAYMENT_ACTIVE_MEMBER:
      case USER_TYPES.BAM_PUBLICSITE_ACTIVE_MEMBER: {
        return API_ROUTES_FOR_ACTIVE_MEMBER;
      }
      case USER_TYPES.BINDER_MEMBER: {
        return API_ROUTES_FOR_BINDER;
      }
      case USER_TYPES.CA_GOVT_USER:
        return API_ROUTES_FOR_CA_GOVT;
      case USER_TYPES.CUSTOMER_ADVOCATE:
        {
          if (isCABinder) return API_ROUTES_FOR_BINDER;
          else return API_ROUTES_FOR_CA;
        }
        break;
      case USER_TYPES.CA_TERMED_MEMBER:
        {
          return API_ROUTES_FOR_CA;
        }
        break;
      case USER_TYPES.GOVERNMENT_GUEST:
        {
          return API_ROUTES_FOR_GOVT_GUEST;
        }
        break;
      case USER_TYPES.NMA_GOVT_USER:
      case USER_TYPES.ONE_TIME_GOVERNMENT_ACTIVE_MEMBER:
      case USER_TYPES.BAM_GOVERNMENT_ACTIVE_MEMBER: {
        return API_ROUTES_FOR_GOVT_ACTIVE;
      }
      case USER_TYPES.BROKER: {
        return API_ROUTES_FOR_BROKER;
      }
      default: {
        return API_ROUTES;
      }
    }
  },
};

export default utils;

function processError(
    err: AxiosError,
    setAPIStatus: ISetApiStatus,
    setGlobalTechnicalDifficulty: (
        _globalTechnicalDifficulty: IGlobalTechnicalDifficulty,
    ) => void,
) {
  const route = err?.config?.url?.split('?')[0] || '';
  const responseData = err?.response?.data as Record<string, any>;

  if (
      (EXCLUSIVE_ERRORCODE_ROUTES[route] &&
          (EXCLUSIVE_ERRORCODE_ROUTES[route].indexOf('**') > -1 ||
              EXCLUSIVE_ERRORCODE_ROUTES[route].indexOf(err.response?.status) > -1)) ||
      (process.env['REACT_APP_FORGEROCK_AUTHORIZE_URI'] &&
          route.indexOf(process.env['REACT_APP_FORGEROCK_AUTHORIZE_URI']) > -1) ||
      (process.env['REACT_APP_FORGEROCK_ACCESS_TOKEN_URI'] &&
          route.indexOf(process.env['REACT_APP_FORGEROCK_ACCESS_TOKEN_URI']) > -1)
  ) {
    setGlobalTechnicalDifficulty({
      errorCode: responseData?.errorCode || '',
    });
    return responseData;
  } else {
    setAPIStatus(API_STATUS_TYPES.SHOW_ERROR_SCREEN, '');
    setGlobalTechnicalDifficulty({
      errorCode: responseData?.errorCode || '',
    });
    return { ...responseData, redirectingToErrorScreen: true };
  }
}

function processLoader(
  setAPIStatus: ISetApiStatus,
  config: InternalAxiosRequestConfig,
  isFetch: boolean,
) {
  const route = config.url || '';
  if (!EXCLUSIVE_LOADER_ROUTES.includes(route)) {
    setAPIStatus(
      API_STATUS_TYPES.LOADER_SCREEN_COUNTER,
      isFetch ? LOADER_SCREEN_ACTION.INCREMENT : LOADER_SCREEN_ACTION.DECREMENT,
    );
  }
}
